import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';

import { Container, Dialog, DialogContent, Tab, Tabs } from '@mui/material';

import { property as propertyGlobals } from '@luxuryescapes/lib-global';

import SuppliersPropertiesPage from '~/components/Bedbank/Properties/Home/SuppliersProperties/PageContainer';
import MappingIssuesPage from '~/components/Bedbank/Properties/MappingIssues/MappingIssuesPage';
import RoomMappingCandidatesPage from '~/components/Bedbank/Properties/MappingIssues/RoomMappingCandidatesPage';
import RoomMappingIssuesPage from '~/components/Bedbank/Properties/MappingIssues/RoomMappingIssuesPage';
import PotentialPropertyMappingsPage from '~/components/Bedbank/Properties/PotentialPropertyMappings/PotentialPropertyMappingsPage';
import PageHeader from '~/components/Common/Elements/PageHeader';
import Image from '~/components/Common/Image';

import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { isAdmin } from '~/utils/adminPermission';

import DynamicConfigurationsPage from './DynamicConfiguration/DynamicConfigurationsPage';
import PageContainer from './PageContainer';

const DYNAMIC_CONFIGURATIONS_TYPE = 'dynamic_configurations';
const MAPPING_ISSUES_TYPE = 'mapping_issues';
const SUPPLIERS_PROPERTIES_TYPE = 'suppliers_properties';
const ROOM_MAPPING_ISSUES_TYPE = 'room_mapping_issues';
const ROOM_MAPPING_CANDIDATES_TYPE = 'room_mapping_candidates';
const POTENTIAL_PROPERTY_MAPPINGS_TYPE = 'potential_property_mappings';

function BedbankPropertiesPage() {
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const { user } = useCurrentUser();
  const userIsAdmin = isAdmin(user);

  const getInitialTab = useCallback(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      return hash;
    }
    return query.get('propertyType') ?? propertyGlobals.HOTEL_TYPE;
  }, [location.hash, query]);

  const [selectedTab, setSelectedTab] = useState(getInitialTab);
  const [selectedImageId, setSelectedImageId] = useState(null);

  const handleTabChange = useCallback(
    (_, value) => {
      setSelectedTab(value);

      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: value,
      });
    },
    [history, location.pathname, location.search],
  );

  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== selectedTab) {
      setSelectedTab(newTab);
    }
  }, [getInitialTab, selectedTab]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Bedbank - Properties</title>
      </Helmet>
      <PageHeader title="Bedbank - Properties" />
      <Tabs sx={{ mb: 2 }} value={selectedTab} onChange={handleTabChange}>
        <Tab
          key={propertyGlobals.HOTEL_TYPE.toLowerCase()}
          value={propertyGlobals.HOTEL_TYPE.toLowerCase()}
          label="Hotels & Resorts"
        />
        <Tab
          key={propertyGlobals.UNIQUE_STAYS_TYPE.toLowerCase()}
          value={propertyGlobals.UNIQUE_STAYS_TYPE.toLowerCase()}
          label="Unique Stays"
        />
        <Tab key={SUPPLIERS_PROPERTIES_TYPE} value={SUPPLIERS_PROPERTIES_TYPE} label="Suppliers Properties" />
        <Tab key={MAPPING_ISSUES_TYPE} value={MAPPING_ISSUES_TYPE} label="Mapping Issues" />
        <Tab key={ROOM_MAPPING_ISSUES_TYPE} value={ROOM_MAPPING_ISSUES_TYPE} label="Room Mapping Issues" />
        <Tab key={ROOM_MAPPING_CANDIDATES_TYPE} value={ROOM_MAPPING_CANDIDATES_TYPE} label="Room Mapping Candidates" />
        <Tab
          key={POTENTIAL_PROPERTY_MAPPINGS_TYPE}
          value={POTENTIAL_PROPERTY_MAPPINGS_TYPE}
          label="Potential Property Mappings"
        />
        {userIsAdmin && (
          <Tab key={DYNAMIC_CONFIGURATIONS_TYPE} value={DYNAMIC_CONFIGURATIONS_TYPE} label="Dynamic Configurations" />
        )}
      </Tabs>

      {selectedTab !== DYNAMIC_CONFIGURATIONS_TYPE &&
        selectedTab !== SUPPLIERS_PROPERTIES_TYPE &&
        selectedTab !== MAPPING_ISSUES_TYPE &&
        selectedTab !== ROOM_MAPPING_ISSUES_TYPE &&
        selectedTab !== ROOM_MAPPING_CANDIDATES_TYPE &&
        selectedTab !== POTENTIAL_PROPERTY_MAPPINGS_TYPE && (
          <>
            <PageContainer
              onOpenModalImage={setSelectedImageId}
              location={location}
              history={history}
              propertyType={selectedTab.toUpperCase()}
            />
            <Dialog
              sx={{
                '& .MuiDialog-container': {
                  '& .MuiPaper-root': {
                    maxWidth: '100%',
                  },
                },
              }}
              open={!!selectedImageId}
              onClose={() => setSelectedImageId(null)}
            >
              <DialogContent sx={{ display: 'flex', height: '90vh' }}>
                {selectedImageId && (
                  <Image className="bedbank-img-responsive" publicId={selectedImageId} options={{ width: 1024 }} />
                )}
              </DialogContent>
            </Dialog>
          </>
        )}

      {selectedTab === DYNAMIC_CONFIGURATIONS_TYPE && userIsAdmin && <DynamicConfigurationsPage />}
      {selectedTab === MAPPING_ISSUES_TYPE && <MappingIssuesPage />}
      {selectedTab === SUPPLIERS_PROPERTIES_TYPE && <SuppliersPropertiesPage location={location} history={history} />}
      {selectedTab === ROOM_MAPPING_ISSUES_TYPE && <RoomMappingIssuesPage />}
      {selectedTab === ROOM_MAPPING_CANDIDATES_TYPE && <RoomMappingCandidatesPage />}
      {selectedTab === POTENTIAL_PROPERTY_MAPPINGS_TYPE && <PotentialPropertyMappingsPage />}
    </Container>
  );
}

export default BedbankPropertiesPage;
